/** Admin · owner cockpit (GET /admin/stats) — whole-platform KPIs, live integrity feed, pipeline. */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { inrCompact, usdCompact } from "../../lib/format.js";
import { Chart, ShieldCheck, Alert, Coins, Arrow, Info } from "../../components/icons.js";

interface AdminStats {
  verifiedThisCycle: number;
  /** null when no cash was collected MTD — a margin over ₹0 cash is meaningless. */
  grossMarginPct: number | null;
  fraudFlags: { type: string; count: number }[];
  activeOrgs: number;
  openDeadLetters: number;
  /** 0032: credit-purchase gross per currency + one ₹-equivalent for the headline (FX_TO_INR). */
  revenue: {
    byCurrency: { currency: string; grossMinor: number; purchases: number }[];
    inrEq: number;
  };
  modelSpendMtdInr: number;
  integrityFeed: { label: string; verdict: "pass" | "flag" | "fail"; ts: string }[];
  requisitionsByState: { state: string; count: number }[];
  signupStats: { today: number; waiting: number; approved: number; rejected: number };
}

interface StatsResponse {
  stats: AdminStats;
}

/**
 * Capacity (GET /admin/capacity) — the answer to "will we hear about a performance problem from a
 * customer first?". Everything in the stack scales itself except the Postgres tier, which has no
 * compute autoscale and degrades quietly when its burst credits run out. Each check therefore
 * carries the command that fixes it; the worst one is rendered as a banner at the top of this page.
 */
interface CapacityCheck {
  id: string;
  level: "ok" | "watch" | "act";
  headline: string;
  detail: string;
  action: string | null;
}
interface CapacityResponse {
  level: "ok" | "watch" | "act";
  checks: CapacityCheck[];
  checkedAt: string;
}

const VERDICT_PILL: Record<string, string> = { pass: "pill v", flag: "pill r", fail: "pill f" };

export function Overview(): ReactNode {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: () => api.get<StatsResponse>("/admin/stats"),
  });
  const stats = data?.stats;

  // Polled, not push: one small query set, and it must stay fresh enough that a backlog building
  // over ten minutes is visible while it still matters (an email OTP dies at ten).
  const capacity = useQuery({
    queryKey: ["admin-capacity"],
    queryFn: () => api.get<CapacityResponse>("/admin/capacity"),
    refetchInterval: 60_000,
  });
  const cap = capacity.data;
  // 'ok' checks are deliberately not shown: a banner that is always on stops being read.
  const notable = (cap?.checks ?? []).filter((c) => c.level !== "ok");

  const fraudTotal = stats ? stats.fraudFlags.reduce((s, f) => s + f.count, 0) : 0;
  const fraudDesc =
    stats && stats.fraudFlags.length > 0
      ? stats.fraudFlags.map((f) => `${f.count} ${f.type.replace(/_/g, " ")}`).join(" · ")
      : "none this cycle";
  const maxReqCount = stats ? Math.max(1, ...stats.requisitionsByState.map((r) => r.count)) : 1;

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Owner cockpit</h1>
          <div className="sub">
            The whole platform, at a glance · owner + platform team · test orgs excluded from every
            money and volume figure · live from the API
          </div>
        </div>
        <span className="pill n">Admin</span>
      </div>

      {/* CAPACITY BANNER. Hidden entirely while healthy. */}
      {cap && cap.level !== "ok" && notable.length > 0 && (
        <div
          className={`banner ${cap.level === "act" ? "crit" : "warn"}`}
          style={{ marginBottom: 16, display: "block" }}
          role="status"
          aria-live="polite"
        >
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <Alert />
            <div style={{ flex: 1 }}>
              <b>
                {cap.level === "act"
                  ? "Capacity: action needed"
                  : "Capacity: worth watching"}
              </b>
              {notable.map((c) => (
                <div key={c.id} style={{ marginTop: 8 }}>
                  <div style={{ fontWeight: 650 }}>{c.headline}</div>
                  <div className="sm" style={{ marginTop: 2 }}>{c.detail}</div>
                  {c.action && (
                    // The remedy travels WITH the warning — a status light nobody knows how to act
                    // on is just anxiety.
                    <pre
                      style={{
                        margin: "6px 0 0",
                        padding: "8px 10px",
                        overflowX: "auto",
                        fontSize: 12,
                        fontFamily: "var(--mono)",
                        background: "var(--surface-2)",
                        borderRadius: 8,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {c.action}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isLoading && <div className="card pad mut sm">Loading platform stats…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load platform stats. Please retry in a moment.</span>
        </div>
      )}

      {stats && (
        <>
          {stats.signupStats && (
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ marginBottom: 16 }}>Signup requests</h3>
              <div className="grid g4">
                <div className="card pad stat">
                <div className="l">Today's requests</div>
                <div className="n tnum">{stats.signupStats.today.toLocaleString("en-IN")}</div>
                <div className="d">created today</div>
              </div>
              <div className="card pad stat">
                <div className="l">Awaiting approval</div>
                <div className="n violet tnum">{stats.signupStats.waiting.toLocaleString("en-IN")}</div>
                <div className="d">free plans to review</div>
              </div>
              <div className="card pad stat">
                <div className="l">Approved</div>
                <div className="n teal tnum">{stats.signupStats.approved.toLocaleString("en-IN")}</div>
                <div className="d">total approved</div>
              </div>
              <div className="card pad stat">
                <div className="l">Rejected</div>
                <div className="n tnum">{stats.signupStats.rejected.toLocaleString("en-IN")}</div>
                <div className="d">total rejected</div>
              </div>
            </div>
            </div>
          )}

          <h3 style={{ marginTop: 24, marginBottom: 16 }}>Platform metrics</h3>

          {stats.openDeadLetters > 0 && (
            <div className="banner crit" style={{ marginBottom: 16 }}>
              <Alert />
              <div>
                <b>{stats.openDeadLetters} dead-letter{stats.openDeadLetters === 1 ? "" : "s"}</b> — outbox
                events failed to dispatch (invites/emails not sent).{" "}
                <a href="/admin/dead-letters" style={{ color: "inherit", textDecoration: "underline" }}>
                  Open the queue →
                </a>
              </div>
            </div>
          )}
          <div className="grid g4" style={{ marginBottom: 16 }}>
            <div className="card pad stat">
              <div className="l">
                <Coins /> Revenue MTD
              </div>
              {/* Credit purchases across all currencies on one axis — ≈ FX for rollup only. */}
              <div className="n violet tnum">{inrCompact(stats.revenue.inrEq)}</div>
              <div className="d">≈ {usdCompact(stats.revenue.inrEq)} · credit purchases, ≈ FX rollup</div>
            </div>
            <div className="card pad stat">
              <div className="l">
                <Chart /> Gross margin
              </div>
              {/* Guard the colour: a negative margin must not render green, and null (no cash
                  collected — e.g. a burn-only month) renders "—", never a green 0%. */}
              <div
                className={`n tnum ${
                  stats.grossMarginPct == null ? "" : stats.grossMarginPct >= 0 ? "good" : "crit"
                }`}
              >
                {stats.grossMarginPct == null ? "—" : `${stats.grossMarginPct}%`}
              </div>
              <div className="d">cash margin · cash collected vs cost of serving · MTD</div>
            </div>
            <div className="card pad stat">
              <div className="l">
                <ShieldCheck /> Verified this cycle
              </div>
              <div className="n teal tnum">{stats.verifiedThisCycle.toLocaleString("en-IN")}</div>
              <div className="d">authoritative post-hoc pipeline</div>
            </div>
            <div className="card pad stat">
              <div className="l">
                <Alert /> Fraud flags caught
              </div>
              <div className="n crit tnum">{fraudTotal}</div>
              <div className="d">{fraudDesc}</div>
            </div>
          </div>

          <div className="grid g4" style={{ marginBottom: 16 }}>
            <div className="card pad stat hl">
              <div className="l">
                Model spend MTD <span className="tag">LLM+ASR</span>
              </div>
              <div className="n violet tnum">{inrCompact(stats.modelSpendMtdInr)}</div>
              <div className="d">≈ {usdCompact(stats.modelSpendMtdInr)} · the line that grows with volume</div>
            </div>
            <div className="card pad stat">
              <div className="l">Active client orgs</div>
              <div className="n tnum">{stats.activeOrgs}</div>
              <div className="d">
                <Link to="/admin/orgs" style={{ color: "inherit" }}>
                  per-company economics →
                </Link>
              </div>
            </div>
          </div>

          <div className="grid" style={{ gridTemplateColumns: "1.6fr 1fr", marginBottom: 16 }}>
            <div className="card">
              <div className="card-h">
                <h3>Requisitions by state</h3>
                {/* Operational, not financial: these two panels DO include test orgs, because a
                    demo run that trips the integrity engine is a result worth seeing. */}
                <span className="tag">Pipeline · all orgs</span>
              </div>
              <div className="card-b">
                {stats.requisitionsByState.length === 0 && (
                  <p className="mut sm" style={{ margin: 0 }}>
                    No requisitions yet.
                  </p>
                )}
                {stats.requisitionsByState.map((r) => (
                  <div key={r.state} className="meter" style={{ marginBottom: 8 }}>
                    <span className="sm mut" style={{ width: 120, textTransform: "capitalize", flex: "none" }}>
                      {r.state.replace(/_/g, " ")}
                    </span>
                    <div className="bar">
                      <i style={{ width: `${(r.count / maxReqCount) * 100}%`, background: "var(--lime-strong)" }} />
                    </div>
                    <span className="val tnum">{r.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-h">
                <h3>Integrity feed</h3>
                <span className="tag">Live · all orgs</span>
              </div>
              <div className="card-b">
                {stats.integrityFeed.length === 0 && (
                  <p className="mut sm" style={{ margin: 0 }}>
                    No integrity signals yet this cycle.
                  </p>
                )}
                {stats.integrityFeed.map((f, i) => (
                  <div key={`${f.ts}-${i}`} className="krow">
                    <span className="k">{f.label}</span>
                    <span className={VERDICT_PILL[f.verdict] ?? "pill n"}>{f.verdict}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-h">
          <h3>Console shortcuts</h3>
        </div>
        <div className="card-b">
          <div className="krow">
            <span className="k">Spend & model cost</span>
            <Link className="btn sm ghost" to="/admin/spend">
              <Arrow /> Open
            </Link>
          </div>
          <div className="krow">
            <span className="k">Releases & versions</span>
            <Link className="btn sm ghost" to="/admin/releases">
              <Arrow /> Open
            </Link>
          </div>
          <div className="krow">
            <span className="k">Access & MFA</span>
            <Link className="btn sm ghost" to="/admin/team">
              <Arrow /> Open
            </Link>
          </div>
          <div className="krow">
            <span className="k">Audit log</span>
            <Link className="btn sm ghost" to="/admin/audit">
              <Arrow /> Open
            </Link>
          </div>
        </div>
      </div>

      <div className="banner info">
        <Info />
        <div>
          Facial AI is <b>proof only</b> — identity, liveness, injection and singular-presence. It never scores
          face, tone, affect or personality. The engine ranks; a human decides, and rejection is human-gated.
        </div>
      </div>
    </section>
  );
}
